import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DeleteModal from "../../components/Common/DeleteModal";
import * as Yup from "yup";
import { useFormik } from "formik";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import withAuth from "../withAuth";
import axios from "axios";
import { encodePartnerCohort, fancyFormatDate, getCohortStatus, handleAuthError } from "../../utils";
import {
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { baseUrl } from '../../Network';
import { usePagination } from "../../hooks/usePagination";
import PaginationControls from "../../components/Common/PaginationControls";
import { useConfirm } from "../../contexts/ConfirmContext";
import { isMarketerReadOnly } from "../../helpers/adminRole";

const ManageCohort = () => {
  document.title = "Manage Partner Cohorts | RYD Admin";
  const { showConfirm } = useConfirm();
  const readOnly = isMarketerReadOnly();

  const [cohorts, setCohorts] = useState([]);
  const [cohortsData, setCohortsData] = useState([]);
  const [changedCohortDates, setChangedCohortDates] = useState([]);
  const [selectedCohort, setSelectedCohort] = useState({});
  const [dateModal, setDateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [emailModal, setEmailModal] = useState(false);
  const [selectedCohortForEmail, setSelectedCohortForEmail] = useState(null);
  const [additionalEmails, setAdditionalEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");

  const {
    paginatedData: paginatedCohorts,
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    showAll,
    handlePageChange,
    handlePageSizeChange,
    goToNextPage,
    goToPreviousPage,
    canPreviousPage,
    canNextPage,
    paginatedDataLength,
  } = usePagination(cohortsData, 20);

  const { id: partnerId } = useParams();

  // Define toggleDateModal BEFORE dateValidation
  const toggleDateModal = () => {
    setDateModal(!dateModal);
  };

  const dateValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      startDate: selectedCohort.startDate ? new Date(selectedCohort.startDate).toISOString().split('T')[0] : "",
      endDate: selectedCohort.endDate ? new Date(selectedCohort.endDate).toISOString().split('T')[0] : "",
    },
    validationSchema: Yup.object({
      startDate: Yup.date().required("Please Enter Start Date"),
      endDate: Yup.date().required("Please Enter End Date").min(
        Yup.ref('startDate'),
        "End date must be after start date"
      ),
    }),

    onSubmit: async (values) => {
      try {
        const changeDateData = {
          partnerId: Number(partnerId || 0),
          cohortId: selectedCohort.id,
          startDate: values.startDate,
          endDate: values.endDate,
        };

        toast.warning("Processing......");

        const response = await axios.post(
          `${baseUrl}/admin/partner/cohort/date`,
          changeDateData
        );

        toast.success("Cohort date changed successfully");

        // Refresh the changed cohort dates
        fetchChangedCohortDates();
        toggleDateModal();
      } catch (error) {
        handleAuthError(error);
        toast.error("Failed to change cohort date");
        console.error(error);
      }
    },
  });

  useEffect(() => {
    fetchCohorts();
    fetchChangedCohortDates();
  }, []);

  const toggleEmailModal = () => {
    if (emailModal) {
      setAdditionalEmails([]);
      setEmailInput("");
    }
    setEmailModal(!emailModal);
  };

  const handleEmailModalOpen = (cohort) => {
    setSelectedCohortForEmail(cohort);
    toggleEmailModal();
  };

  const addEmail = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailInput && emailRegex.test(emailInput)) {
      if (!additionalEmails.includes(emailInput)) {
        setAdditionalEmails([...additionalEmails, emailInput]);
        setEmailInput("");
      } else {
        toast.warning("Email already added");
      }
    } else {
      toast.error("Please enter a valid email");
    }
  };

  const removeEmail = (email) => {
    setAdditionalEmails(additionalEmails.filter(e => e !== email));
  };

  const handleSendEmail = () => {
    handleMailing(selectedCohortForEmail);
    toggleEmailModal();
  };

  const fetchCohorts = async () => {
    try {
      const response = await axios.get(`${baseUrl}/admin/partner/cohort/${partnerId}`);
      setCohorts(response.data.data);
      setCohortsData(response.data.data);
      setLoading(false);
    } catch (error) {
      handleAuthError(error);
      setLoading(false);
      console.error(error);
    }
  };

  const fetchChangedCohortDates = async () => {
    try {
      const response = await axios.get(`${baseUrl}/admin/partner/cohort/date/${partnerId}`);
      setChangedCohortDates(response.data.data);
    } catch (error) {
      handleAuthError(error);
      console.error(error);
    }
  };

  const handleMailing = async (cohort) => {
    try {
      const confirmed = await showConfirm({ title: "Send email", message: "Are you sure you want to send this email?", variant: "primary" });
      if (confirmed) {
        const code = encodePartnerCohort(partnerId, cohort.id);
        const payload = {
          additionalEmails: additionalEmails.length > 0 ? additionalEmails : undefined
        };

        const response = await axios.post(`${baseUrl}/admin/partner/invoice/${code}`, payload);
        toast.warning("Mailing......");

        if (response.data.status) {
          toast.success("Invoice email sent successfully");
          setAdditionalEmails([]);
        } else {
          toast.error("Sorry this cohort did not have any active programs, Try again later");
        }
      }
    } catch (error) {
      handleAuthError(error);
      console.error(error);
    }
  };

  const handleDateChange = (cohort) => {
    setSelectedCohort(cohort);
    toggleDateModal();
  };

  const getEffectiveDates = (cohort) => {
    // Find if there's a changed date for this cohort
    const changedDate = changedCohortDates.find(cd => cd.cohortId === cohort.id);

    if (changedDate) {
      return {
        startDate: changedDate.startDate,
        endDate: changedDate.endDate,
        isChanged: true
      };
    }

    return {
      startDate: cohort.startDate,
      endDate: cohort.endDate,
      isChanged: false
    };
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Manage Cohorts" />
          <div>
            <Link to="/manage-partner">
              <button className="btn btn-primary mb-3">
                <i className="mdi mdi-arrow-left me-1"></i>
                Manage Partners
              </button>
            </Link>
          </div>
          <Row className="align-items-center">
            <Col md={6}>
              <div className="mb-3">
                <h5 className="card-title">
                  Partner Cohorts{" "}
                  <span className="text-muted fw-normal ms-2">
                    ({cohorts.length})
                  </span>
                </h5>
              </div>
            </Col>
            <Col md={6}>
              <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 mb-3">
                <div style={{ width: 200 }}>
                  <select
                    className={'form-control'}
                    onChange={(e) => {
                      if (Number(e.target.value) === 1) {
                        setCohortsData(cohorts.filter(r => r.isStarted === true));
                      } else if (Number(e.target.value) === 2) {
                        setCohortsData(cohorts.filter(r => r.notStarted === true));
                      } else {
                        setCohortsData(cohorts);
                      }
                    }}
                  >
                    <option value={0}>All Cohorts</option>
                    <option value={1}>Started Cohorts</option>
                    <option value={2}>Not Started Cohorts</option>
                  </select>
                </div>
                <div>
                  <Input
                    type="text"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => {
                      setCohortsData(cohorts.filter((cohort) =>
                        cohort.title.toLowerCase().includes(e.target.value.toLowerCase()) ||
                        cohort.description.toLowerCase().includes(e.target.value.toLowerCase())
                      ));
                    }}
                  />
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col lg="12">
              <Row>
                <Col xl="12">
                  {loading ? (
                    <div className="text-center mt-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : cohortsData.length === 0 ? (
                    <div className="text-center mt-5">
                      <h3>No cohorts available</h3>
                    </div>
                  ) : (
                    <table className="table align-middle">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Title</th>
                          <th>Description</th>
                          <th>Start Date</th>
                          <th>End Date</th>
                          <th>Status</th>
                          <th>Date Changed</th>
                          {!readOnly && <th>Action</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedCohorts.map((cohort, index) => {
                          const globalIndex = showAll 
                            ? index 
                            : (currentPage - 1) * pageSize + index;
                          const effectiveDates = getEffectiveDates(cohort);
                          return (
                            <tr
                              key={cohort.id || index}
                              style={{
                                backgroundColor: effectiveDates.isChanged ? '#fff3cd' : '#fff'
                              }}
                            >
                              <td>{globalIndex + 1}</td>
                              <td>{cohort.title}</td>
                              <td>{cohort.description}</td>
                              <td>{fancyFormatDate(effectiveDates.startDate)}</td>
                              <td>{fancyFormatDate(effectiveDates.endDate)}</td>
                              <td>
                                {(() => {
                                  const status = getCohortStatus(effectiveDates);

                                  if (status === "completed") {
                                    return (
                                      <span className="badge rounded-pill bg-primary">
                                        Completed
                                      </span>
                                    );
                                  } else if (status === "ongoing") {
                                    return (
                                      <span className="badge rounded-pill bg-success">
                                        Ongoing
                                      </span>
                                    );
                                  } else if (status === "not-started") {
                                    return (
                                      <span className="badge rounded-pill bg-warning">
                                        Not Started
                                      </span>
                                    );
                                  }
                                  return null;
                                })()}
                              </td>

                              <td>
                                {effectiveDates.isChanged ? (
                                  <span className="badge bg-info">Yes</span>
                                ) : (
                                  <span className="badge bg-secondary">No</span>
                                )}
                              </td>
                              {!readOnly && (
                              <td>
                                <div className="d-flex gap-3">
                                  <Link
                                    className="text-primary"
                                    to="#"
                                    onClick={() => handleDateChange(cohort)}
                                    title="Change Date"
                                  >
                                    <i className="mdi mdi-calendar-edit font-size-18"></i>
                                  </Link>
                                  <Link
                                    className="text-secondary"
                                    to="#"
                                    onClick={() => handleEmailModalOpen(cohort)}
                                    title="Send invoice payment email for this cohort"
                                  >
                                    <i className="mdi mdi-mail font-size-18"></i>
                                  </Link>
                                </div>
                              </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                  {cohortsData.length > 0 && (
                    <PaginationControls
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalItems={totalItems}
                      pageSize={pageSize}
                      showAll={showAll}
                      handlePageChange={handlePageChange}
                      handlePageSizeChange={handlePageSizeChange}
                      goToNextPage={goToNextPage}
                      goToPreviousPage={goToPreviousPage}
                      canPreviousPage={canPreviousPage}
                      canNextPage={canNextPage}
                      paginatedDataLength={paginatedDataLength}
                    />
                  )}

                  {!readOnly && <>
                  {/* Email Modal */}
                  <Modal isOpen={emailModal} toggle={toggleEmailModal}>
                    <ModalHeader toggle={toggleEmailModal}>
                      Send Invoice Email - {selectedCohortForEmail?.title}
                    </ModalHeader>
                    <ModalBody>
                      <div className="mb-3">
                        <Label className="form-label">Additional Email Recipients (Optional)</Label>
                        <div className="d-flex gap-2">
                          <Input
                            type="email"
                            placeholder="Enter email address"
                            value={emailInput}
                            onChange={(e) => setEmailInput(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addEmail();
                              }
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={addEmail}
                          >
                            Add
                          </button>
                        </div>
                      </div>

                      {additionalEmails.length > 0 && (
                        <div className="mb-3">
                          <Label className="form-label">Added Emails:</Label>
                          <div className="d-flex flex-wrap gap-2">
                            {additionalEmails.map((email, idx) => (
                              <span key={idx} className="badge bg-info d-flex align-items-center gap-2">
                                {email}
                                <i
                                  className="mdi mdi-close cursor-pointer"
                                  onClick={() => removeEmail(email)}
                                  style={{ cursor: 'pointer' }}
                                ></i>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="text-end">
                        <button
                          type="button"
                          className="btn btn-secondary me-2"
                          onClick={toggleEmailModal}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleSendEmail}
                        >
                          Send Email
                        </button>
                      </div>
                    </ModalBody>
                  </Modal>

                  {/* Date Change Modal */}
                  <Modal isOpen={dateModal} toggle={toggleDateModal}>
                    <ModalHeader toggle={toggleDateModal}>
                      Change Cohort Date - {selectedCohort.title}
                    </ModalHeader>
                    <ModalBody>
                      <Form onSubmit={dateValidation.handleSubmit}>
                        <Row>
                          <Col xs={12}>
                            <div className="mb-3">
                              <Label className="form-label">Start Date</Label>
                              <Input
                                name="startDate"
                                type="date"
                                onChange={dateValidation.handleChange}
                                onBlur={dateValidation.handleBlur}
                                value={dateValidation.values.startDate || ""}
                                invalid={
                                  dateValidation.touched.startDate &&
                                  dateValidation.errors.startDate
                                }
                              />
                              <FormFeedback type="invalid">
                                {dateValidation.errors.startDate}
                              </FormFeedback>
                            </div>
                            <div className="mb-3">
                              <Label className="form-label">End Date</Label>
                              <Input
                                name="endDate"
                                type="date"
                                onChange={dateValidation.handleChange}
                                onBlur={dateValidation.handleBlur}
                                value={dateValidation.values.endDate || ""}
                                invalid={
                                  dateValidation.touched.endDate &&
                                  dateValidation.errors.endDate
                                }
                              />
                              <FormFeedback type="invalid">
                                {dateValidation.errors.endDate}
                              </FormFeedback>
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            <div className="text-end">
                              <button
                                type="button"
                                className="btn btn-secondary me-2"
                                onClick={toggleDateModal}
                              >
                                Cancel
                              </button>
                              <button
                                type="submit"
                                className="btn btn-primary"
                              >
                                Change Date
                              </button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </ModalBody>
                  </Modal>
                  </>}
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
};

export default withAuth(ManageCohort);